import { NavLink, Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { HeaderMinuteChip } from './MinuteQuota.jsx';
import { useTenant } from './TenantContext.jsx';
import {
    Building2, PhoneCall, BookUser, Tag, LogOut, ChevronDown, Users, Contact, PhoneOutgoing, UserCircle2, Activity, MessageSquare,
    PanelLeftClose, PanelLeftOpen, ClipboardList, BarChart3, Layers, Code2, Coins, Timer, ShieldCheck, KeyRound,
    Ticket, Stethoscope, Wrench, BookOpen, Puzzle,
} from 'lucide-react';

// 真人客服 Chrome 外掛「Qubby 客服台」介紹頁（頁內有安裝按鈕 → Chrome 線上應用程式商店）
const AGENT_EXT_URL = 'https://home.qubby.io/agent-extension';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SystemWarnings from './SystemWarnings.jsx';
import VersionBadge from './VersionBadge.jsx';
import { apiAccess } from '../lib/apiAccess.js';
import LanguageSwitcher from './LanguageSwitcher.jsx';
import { ChangePasswordModal } from './ChangePasswordModal.jsx';
import { tenantsApi } from '../api/tenants.js';

// adminOnly: true → 只有 role=admin (母帳號) 看得到；子帳號隱藏
// key: nav namespace 的翻譯 key
const navItems = [
    // 超管帳號只看到這三項（無 tenant 綁定，一般租戶頁對它沒有意義）
    { to: '/admin', key: 'superAdmin', icon: Activity, superAdminOnly: true },
    { to: '/admin/data', key: 'superAdminData', icon: BarChart3, superAdminOnly: true },
    { to: '/admin/business', key: 'superAdminBusiness', icon: Contact, superAdminOnly: true },
    { to: '/admin/billing', key: 'superAdminBilling', icon: Coins, superAdminOnly: true },
    { to: '/admin/minutes', key: 'superAdminMinutes', icon: Timer, superAdminOnly: true },
    { to: '/admin/tickets', key: 'superAdminTickets', icon: Ticket, superAdminOnly: true },
    // 東訊大盤商 — 不放頂層 nav，改從 tecom 租戶頁進入（/admin/tenants/tecom）
    { to: '/tenants', key: 'tenants', icon: Building2, adminOnly: true },
    { to: '/ivr-agents', key: 'ivrAgents', icon: MessageSquare, adminOnly: false },
    // csOnly：無 CS（智慧客服）席次時灰掉禁用（收單 / 外撥 / 客戶 / 問卷資料 / API 文件）
    { to: '/leads', key: 'leads', icon: ClipboardList, adminOnly: false, csOnly: true },
    { to: '/outbound', key: 'outbound', icon: PhoneOutgoing, adminOnly: false, csOnly: true },
    { to: '/directory', key: 'directory', icon: BookUser, adminOnly: true },
    { to: '/calls', key: 'calls', icon: PhoneCall, adminOnly: false },
    { to: '/customers', key: 'customers', icon: Contact, adminOnly: false, csOnly: true },
    { to: '/tags', key: 'tags', icon: Tag, adminOnly: true },
    { to: '/members', key: 'members', icon: Users, adminOnly: true },
    // 授權續約/加購 — 只給東訊來源商用帳號（partnerSource=sdx500）；label 寫死
    { to: '/my-license', key: 'myLicense', label: '授權續約 / 加購', icon: KeyRound, sdx500Only: true },
    // 東訊授權管理 — 只給 @tecom.com 帳號（admin@tecom.com）看；label 寫死（TECOM 內部 zh-TW）
    { to: '/sdx500', key: 'sdx500', label: '東訊設備授權管理', icon: ShieldCheck, tecomOnly: true },
    { to: '/survey-data', key: 'surveyData', icon: ClipboardList, adminOnly: false, csOnly: true },
    { to: '/dashboard', key: 'dashboard', icon: BarChart3, adminOnly: false },
    { to: '/plans', key: 'plans', icon: Layers, adminOnly: true, dealerHidden: true },
    // 經銷商診斷 — 只有經銷商 (accountRole=dealer) 看得到；放在「API 文件」上方
    { to: '/dealer/diagnose', key: 'dealerDiagnose', icon: Stethoscope, dealerOnly: true },
    { to: '/api-docs', key: 'apiDocs', icon: Code2, adminOnly: true, csOnly: true },
    // 操作手冊 — 外部連結（官網 home.qubby.io 上的靜態指南），新分頁開啟，所有角色可見
    { external: 'https://home.qubby.io/guide/admin-console.html', key: 'guide', icon: BookOpen, adminOnly: false },
];

const SIDEBAR_KEY = 'qubby-admin:sidebarCollapsed';

export default function Layout() {
    const { t } = useTranslation('nav');
    const { user, signOut } = useAuth();
    // 只有「email/密碼」帳號才有密碼可改；Google 登入帳號沒有密碼 → 不顯示
    const hasPassword = !!user?.providerData?.some((p) => p.providerId === 'password');
    const [pwModalOpen, setPwModalOpen] = useState(false);
    const { tenants, hostId, setHostId, loading: tenantLoading, isAdmin, isSuperAdmin, accountRole, role, hasCS, isSdx500 } = useTenant();
    const nav = useNavigate();
    const currentTenant = tenants.find((t) => t.hostId === hostId);
    // 超管 = 純超管視角：只看三個超管頁（租戶級頁面對無 tenant 的超管全是空殼）+ 授權管理當後路
    // dealerHidden：經銷商 (accountRole=dealer) 不該看到的頁（如方案總覽）→ 隱藏 nav
    // 授權管理（tecomOnly）2026-07-09 起限「專職帳號 license_admin@tecom.com」；該帳號只看授權管理一頁。
    const email = (user?.email || '').toLowerCase();
    const isLicenseAdmin = email === 'license_admin@tecom.com';
    const visibleNav = isSuperAdmin
        ? navItems.filter((it) => it.superAdminOnly || it.key === 'sdx500')
        : isLicenseAdmin
            ? navItems.filter((it) => it.key === 'sdx500')   // 專職授權管理帳號：只看授權管理
            : navItems.filter((it) => !it.superAdminOnly && (!it.adminOnly || isAdmin)
                && !(it.dealerHidden && accountRole === 'dealer')
                && (!it.dealerOnly || accountRole === 'dealer')
                && (!it.sdx500Only || isSdx500)   // 續約/加購頁只給東訊來源商用帳號
                && (!it.csOnly || hasCS)          // 沒買 CS 席次 → CS 專屬項直接隱藏（不灰掉禁用）
                && (it.key !== 'apiDocs' || apiAccess(accountRole, currentTenant).docs)   // 商用帳號要超管開通才顯示
                && !it.tecomOnly);   // 非專職帳號（含 admin@tecom.com）不再顯示授權管理

    // 進客服 workspace 自動收起（最大化 3 欄空間），離開回用戶儲存的偏好
    const location = useLocation();
    const isWorkspace = /^\/ivr-agents\/[^/]+/.test(location.pathname);

    // userPref：使用者點 toggle 寫進 localStorage 的偏好
    const [userPref, setUserPref] = useState(
        () => localStorage.getItem(SIDEBAR_KEY) === '1'
    );
    // 實際顯示狀態：workspace 強制收起，否則跟 userPref
    const [forceCollapsed, setForceCollapsed] = useState(isWorkspace);
    useEffect(() => {
        setForceCollapsed(isWorkspace);
    }, [isWorkspace]);

    const sidebarCollapsed = forceCollapsed || userPref;
    const toggleSidebar = () => {
        if (forceCollapsed) {
            // workspace 時點開 → 暫時釋放強制（這個 navigation cycle 內展開）
            setForceCollapsed(false);
            return;
        }
        // 一般狀態 → 切 userPref 並持久化
        setUserPref((p) => {
            const next = !p;
            localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0');
            return next;
        });
    };

    // 超管殼層防呆：切帳號後 SPA 可能停在切換前的租戶級頁（對無 membership 的超管全是空殼）→ 一律導回超管中心
    // 例外：超管可存取的非超管頁（授權管理 /sdx500 當後路、徽章「查看完整系統狀態」的 /system）不擋。
    if (isSuperAdmin && !location.pathname.startsWith('/admin')
        && !location.pathname.startsWith('/dealer') && !location.pathname.startsWith('/sdx500')
        && location.pathname !== '/system') {
        return <Navigate to="/admin" replace />;
    }

    return (
        <div className="min-h-dvh flex flex-col bg-cream-50">
            {/* ── Top brand bar ── */}
            <header className="border-b border-cream-200 bg-cream-50/95 backdrop-blur-sm sticky top-0 z-20">
                <div className="px-8 py-4 flex items-center gap-8">
                    <div className="flex items-center gap-3">
                        <NavLink to="/ivr-agents" title={t('brand.home')} className="shrink-0 flex items-center gap-2.5 transition hover:opacity-80">
                            <img src="/qubby-icon.png" alt="" className="h-11 w-11 select-none" draggable={false} />
                            <span className="heading-display text-3xl font-semibold text-ink-900 tracking-tight">QubbyAI</span>
                        </NavLink>
                    </div>

                    {/* Tenant switcher — 超管無 tenant 綁定，不顯示 */}
                    {!isSuperAdmin && (
                        <TenantSwitcher
                            tenants={tenants}
                            hostId={hostId}
                            currentTenant={currentTenant}
                            onChange={(id) => { setHostId(id); nav('/tenants'); }}   // 切換 tenant 後回租戶設定頁（避免停在前一個 tenant 的 detail）
                            loading={tenantLoading}
                        />
                    )}

                    <div className="flex-1" />

                    {/* 系統狀態徽章 + 版號。
                        0724 曾整顆收成 super_admin 限定（怕經銷/商用看到別家 tenant 紅字誤以為自己壞了），
                        但徽章內容早已由 08ff2c4 縮到「目前選的 tenant」，且版號寄生其中一併消失 → 全帳號恢復顯示。
                        版號獨立成 VersionBadge：哨兵 API 掛掉時徽章 return null，版號仍看得到。 */}
                    {/* 預付帳號才會顯示（後付 / 超管：元件自己 return null） */}
                    {!isSuperAdmin && <HeaderMinuteChip />}

                    <div className="flex items-center gap-2">
                        <SystemWarnings />
                        <VersionBadge />
                    </div>

                    {/* 真人客服 Chrome 外掛下載 — 連 Chrome 線上應用程式商店 */}
                    <a
                        href={AGENT_EXT_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={t('agentExt')}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-500 hover:text-accent-700 transition px-2.5 py-1.5 rounded-full hover:bg-cream-100"
                    >
                        <Puzzle size={14} /> <span className="hidden lg:inline">{t('agentExt')}</span>
                    </a>

                    {/* 語系切換器 — 右上角 */}
                    <LanguageSwitcher variant="header" />

                    <span className="h-6 w-px bg-cream-300/70 mx-0.5" aria-hidden="true" />

                    {/* User */}
                    <div className="flex items-center gap-2.5">
                        {user?.photoURL && (
                            <img
                                src={user.photoURL}
                                alt=""
                                className="size-8 rounded-full ring-1 ring-cream-200"
                            />
                        )}
                        <div className="text-right leading-tight">
                            <div className="text-sm text-ink-700 font-medium truncate max-w-[12rem]">
                                {user?.displayName || user?.email}
                            </div>
                            <div className="flex flex-col items-end gap-1">
                                {hasPassword && (
                                    <button
                                        onClick={() => setPwModalOpen(true)}
                                        className="text-xs text-ink-400 hover:text-accent-700 transition flex items-center gap-1"
                                    >
                                        <KeyRound size={12} /> {t('common:pw.title')}
                                    </button>
                                )}
                                <button
                                    onClick={async () => {
                                        await signOut();
                                        nav('/login');
                                    }}
                                    className="text-xs text-ink-400 hover:text-accent-700 transition flex items-center gap-1"
                                >
                                    <LogOut size={12} /> {t('common:signOut')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <ChangePasswordModal open={pwModalOpen} onClose={() => setPwModalOpen(false)} />

            <div className="flex flex-1 relative">
                {/* ── Side nav ── */}
                <nav
                    className={[
                        'border-r border-cream-200 py-6 shrink-0 bg-cream-50 transition-all duration-200',
                        sidebarCollapsed ? 'w-14 px-1' : 'w-60 px-3',
                        // 釘住視窗高度 — 否則 nav 會被超長的內容（角色清單）撐高，
                        // mt-auto 的方案/角色卡片就被拖到畫面外
                        'flex flex-col sticky top-[76px] self-start h-[calc(100dvh-76px)] overflow-hidden',
                    ].join(' ')}
                >
                    {!sidebarCollapsed && (
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400 px-3 mb-3">
                            {t('section.manage')}
                        </div>
                    )}
                    <ul className="space-y-0.5 flex-1 min-h-0 overflow-y-auto">
                        {visibleNav.map(({ to, key, icon: Icon, label: hardLabel, external }) => {
                            const label = hardLabel || t(key);

                            // 「客服診斷」不在這條普通清單渲染 — 改成底部「方案」卡上方的獨立工具箱按鈕（見下方）。
                            if (key === 'dealerDiagnose') return null;

                            // 注意：CS 專屬項（csOnly）在沒買 CS 席次時已於上方 visibleNav 過濾掉（直接隱藏，不再灰掉禁用）。

                            // 外部連結（操作手冊）→ 用 <a> 開新分頁，非 react-router 路由
                            if (external) {
                                return (
                                    <li key={external}>
                                        <a href={external} target="_blank" rel="noopener noreferrer"
                                            title={sidebarCollapsed ? label : undefined}
                                            className={[
                                                'flex items-center rounded-md text-sm transition text-ink-700 hover:bg-cream-100',
                                                sidebarCollapsed ? 'justify-center py-2.5' : 'gap-3 px-3 py-2',
                                            ].join(' ')}
                                        >
                                            <Icon size={16} strokeWidth={1.7} className="shrink-0" />
                                            {!sidebarCollapsed && (
                                                <span className="leading-tight line-clamp-2">{label}</span>
                                            )}
                                        </a>
                                    </li>
                                );
                            }

                            return (
                                <li key={to}>
                                    <NavLink
                                        to={to}
                                        end={to === '/admin'}
                                        title={sidebarCollapsed ? label : undefined}
                                        className={({ isActive }) =>
                                            [
                                                'flex items-center rounded-md text-sm transition',
                                                sidebarCollapsed ? 'justify-center py-2.5' : 'gap-3 px-3 py-2',
                                                isActive
                                                    ? 'bg-accent-50 text-accent-700 font-medium'
                                                    : 'text-ink-700 hover:bg-cream-100',
                                            ].join(' ')
                                        }
                                    >
                                        <Icon size={16} strokeWidth={1.7} className="shrink-0" />
                                        {!sidebarCollapsed && (
                                            <span className="leading-tight line-clamp-2">{label}</span>
                                        )}
                                    </NavLink>
                                </li>
                            );
                        })}
                    </ul>
                    {/* ── 側欄底部：方案用量 + 角色 + 客服診斷工具箱 ── */}
                    <div className="mt-auto pt-4 space-y-2">
                        {isAdmin && !sidebarCollapsed && <AvatarQuotaPill hostId={hostId} />}
                        {/* 角色卡只給子帳號看；母帳號(admin)不顯示 */}
                        {role && role !== 'admin' && !sidebarCollapsed && (
                            <div className="px-3 py-2 bg-cream-100 border border-cream-200 shadow-sm rounded-lg">
                                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400">
                                    {t('role.label')}
                                </div>
                                <div className="text-sm font-medium text-ink-900 mt-0.5">
                                    {t('role.sub')}
                                </div>
                            </div>
                        )}
                        {/* 客服診斷 = 經銷商專屬維修診斷工具。放在「方案／角色」卡之後（錨在最底，方案卡非同步
                            載入時不會把它往上推）。米底凸起卡片（邊框+陰影=看得出可按）+ accent 扳手 chip
                            + 小字說明用途（一秒看出是維修診斷工具，不整顆鋪紅）。 */}
                        {accountRole === 'dealer' && (
                            <NavLink
                                to="/dealer/diagnose"
                                title={sidebarCollapsed ? t('dealerDiagnose') : undefined}
                                className={({ isActive }) =>
                                    [
                                        'group flex items-center rounded-lg border shadow-sm transition-all',
                                        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500',
                                        sidebarCollapsed ? 'justify-center p-1.5' : 'gap-2.5 px-2.5 py-2',
                                        'bg-white',
                                        isActive
                                            ? 'border-accent-300 shadow-md'
                                            : 'border-cream-300 hover:border-accent-300 hover:shadow-md',
                                    ].join(' ')
                                }
                            >
                                <span
                                    className={[
                                        'grid place-items-center rounded-lg bg-accent-500 text-white shadow-sm',
                                        'transition-transform group-hover:scale-105',
                                        sidebarCollapsed ? 'size-8' : 'size-[30px] shrink-0',
                                    ].join(' ')}
                                >
                                    <Wrench size={16} strokeWidth={2.1} />
                                </span>
                                {!sidebarCollapsed && (
                                    <span className="flex flex-col leading-tight min-w-0">
                                        <span className="text-sm font-semibold text-ink-900 truncate">{t('dealerDiagnose')}</span>
                                        <span className="text-[11px] text-ink-400 truncate">{t('dealerDiagnoseSub')}</span>
                                    </span>
                                )}
                            </NavLink>
                        )}
                    </div>
                </nav>

                {/* ── Collapse toggle — 浮在 sidebar 跟 main 中間 ── */}
                <button
                    onClick={toggleSidebar}
                    title={sidebarCollapsed ? t('toggle.expand') : t('toggle.collapse')}
                    className="absolute top-3 z-10 size-7 rounded-full bg-white border border-cream-200 shadow-sm hover:bg-cream-100 hover:border-accent-300 transition flex items-center justify-center text-ink-500"
                    style={{ left: sidebarCollapsed ? '42px' : '226px' }}
                >
                    {sidebarCollapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
                </button>

                {/* ── Main ── */}
                <main className="flex-1 px-10 py-8 overflow-x-hidden">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}

function SharedBusinessBadge({ className = '' }) {
    const { t } = useTranslation('nav');
    return (
        <span
            className={`inline-flex items-center px-1.5 py-0.5 rounded-full bg-accent-50 text-accent-700 border border-accent-300/60 font-mono text-[9px] uppercase tracking-[0.12em] leading-none ${className}`}
            title={t('sharedBusiness.title')}
        >
            {t('sharedBusiness.badge')}
        </span>
    );
}

function TenantSwitcher({ tenants, hostId, currentTenant, onChange, loading }) {
    const [open, setOpen] = useState(false);
    if (loading) {
        return (
            <div className="flex items-center gap-2 text-ink-400 font-mono text-xs px-3 py-1.5 rounded border border-cream-200 bg-cream-100">
                loading tenants…
            </div>
        );
    }
    if (tenants.length === 0) {
        return (
            <div className="text-ink-400 font-mono text-xs">no tenants</div>
        );
    }
    return (
        <div className="relative">
            <button
                onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-cream-200 bg-white hover:bg-cream-100 transition"
            >
                <Building2 size={14} className="text-accent-500" strokeWidth={1.8} />
                <div className="text-left">
                    <div className="text-xs text-ink-400 font-mono leading-none">tenant</div>
                    <div className="text-sm font-medium text-ink-900 mt-0.5 leading-none flex items-center gap-1.5">
                        {currentTenant?.displayName || hostId || 'select'}
                        {currentTenant?._sharedBusiness && <SharedBusinessBadge />}
                    </div>
                </div>
                <ChevronDown size={14} className="text-ink-400 ml-1" />
            </button>
            {open && (
                <ul className="absolute top-full left-0 mt-1 w-64 bg-white border border-cream-200 rounded-md shadow-lg z-30 py-1 max-h-80 overflow-y-auto">
                    {tenants.map((t) => (
                        <li key={t.hostId}>
                            <button
                                onClick={() => {
                                    onChange(t.hostId);
                                    setOpen(false);
                                }}
                                className={[
                                    'w-full text-left px-3 py-2 hover:bg-cream-100 transition flex items-center justify-between',
                                    t.hostId === hostId ? 'bg-cream-100' : '',
                                ].join(' ')}
                            >
                                <div>
                                    <div className="text-sm font-medium text-ink-900 flex items-center gap-1.5">
                                        {t.displayName}
                                        {t._sharedBusiness && <SharedBusinessBadge />}
                                    </div>
                                    <div className="font-mono text-[10px] text-ink-400 mt-0.5">{t.hostId}</div>
                                </div>
                                {t.hostId === hostId && (
                                    <span className="text-accent-500 text-xs">●</span>
                                )}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function AvatarQuotaPill({ hostId }) {
    const { t } = useTranslation('nav');
    const [quota, setQuota] = useState(null);
    const [bump, setBump] = useState(0);
    useEffect(() => {
        if (!hostId) { setQuota(null); return; }
        let cancelled = false;
        tenantsApi.quota(hostId)
            .then((q) => { if (!cancelled) setQuota(q); })
            .catch(() => { if (!cancelled) setQuota(null); });
        return () => { cancelled = true; };
    }, [hostId, bump]);
    // 全域 event：avatar 新增 / 刪除 / 啟用切換時觸發 → quota refresh
    useEffect(() => {
        const handler = () => setBump((n) => n + 1);
        window.addEventListener('qubby:avatar-changed', handler);
        return () => window.removeEventListener('qubby:avatar-changed', handler);
    }, []);
    if (!quota) return null;
    const { avatarUsed, avatarQuota, planName, plan, accountRole } = quota;
    // 經銷商：slots 不設上限只記錄用量；不顯示方案名稱，顯示「經銷商方案」
    if (accountRole === 'dealer') {
        return (
            <div
                className="px-3 py-2.5 rounded-lg bg-white border border-cream-200 shadow-sm"
                title={`${t('quota.plan')}: ${t('quota.dealerPlan')}\n${t('quota.usageHint')}`}
            >
                <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400">{t('quota.plan')}</span>
                    <span className="text-[11px] font-semibold tnum text-ink-900">{avatarUsed}</span>
                </div>
                <div className="flex items-center gap-1.5 mt-1">
                    <UserCircle2 size={14} strokeWidth={1.8} className="text-accent-500 shrink-0" />
                    <span className="text-[12px] font-medium text-ink-900 truncate">{t('quota.dealerPlan')}</span>
                </div>
            </div>
        );
    }
    if (avatarQuota == null) return null;
    // 方案名稱依 plan id 翻譯（後端硬編中文）；跨 namespace 用 plans:
    const planNameI18n = plan ? t('plans:name.' + plan, { defaultValue: planName }) : planName;
    const pct = avatarQuota > 0 ? Math.min(100, (avatarUsed / avatarQuota) * 100) : 0;
    const isFull = avatarUsed >= avatarQuota;
    const isNear = avatarUsed >= avatarQuota * 0.8 && !isFull;
    const barColor = isFull ? 'bg-danger-500' : isNear ? 'bg-warn-500' : 'bg-accent-500';
    const accentText = isFull ? 'text-danger-500' : isNear ? 'text-warn-500' : 'text-ink-900';
    // 側欄底部全寬「方案」卡片
    return (
        <div
            className="px-3 py-2.5 rounded-lg bg-white border border-cream-200 shadow-sm"
            title={`${t('quota.plan')}: ${planNameI18n || t('quota.notSet')}${plan ? ` (${plan})` : ''}\n${t('quota.usageHint')}`}
        >
            <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400">{t('quota.plan')}</span>
                <span className={`text-[11px] font-semibold tnum ${accentText}`}>
                    {avatarUsed}<span className="text-ink-400 font-normal mx-0.5">/</span>{avatarQuota}
                </span>
            </div>
            <div className="flex items-center gap-1.5 mt-1">
                <UserCircle2 size={14} strokeWidth={1.8} className="text-accent-500 shrink-0" />
                <span className="text-[12px] font-medium text-ink-900 truncate">
                    {planNameI18n || t('quota.noPlan')}
                </span>
            </div>
            <div className="w-full h-1.5 bg-cream-200 rounded-full overflow-hidden mt-2">
                <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}
